import {
  At,
  Broadcast,
  Bug,
  CaretLeft,
  CaretRight,
  Gear,
  MagnifyingGlass,
  MapTrifold,
  Plus,
  Question,
  SignOut,
} from '@phosphor-icons/react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {selectIsAdmin, useUserLoggedIn} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router';
import Logo from '../../components/Logo';
import {navigation} from '../../theme/tokens';

type NavRailProps = {
  expanded: boolean;
  onToggle: () => void;
};

type NavItem = {
  icon: React.ReactNode;
  label: string;
  path: string;
  matchPath?: string;
};

const NavRail: React.FC<NavRailProps> = ({expanded, onToggle}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const isLoggedIn = useUserLoggedIn();
  const isAdmin = useSelector(selectIsAdmin);

  const handleLogout = React.useCallback(() => {
    dispatch({type: 'LOGOUT'});
    navigate('/');
  }, [dispatch, navigate]);

  const railWidth = expanded
    ? navigation.railExpandedWidth
    : navigation.railWidth;

  const mainNavItems: NavItem[] = [
    {
      icon: <MagnifyingGlass />,
      label: formatMessage(messages.stations.searchForLocation),
      path: '/app/stations/search',
      matchPath: '/app/stations/search',
    },
    {
      icon: <Broadcast />,
      label: formatMessage(messages.stations.weatherStations),
      path: '/app/stations/',
      matchPath: '/app/stations/',
    },
    {
      icon: <MapTrifold />,
      label: formatMessage(messages.weatherMap.weatherMap),
      path: '/map/',
    },
    // Forecast maps ("Cartes de prévision") live in the top nav bar
    // (AppNavBar), not here — it's a full-width page like the weather map.
    {
      icon: <Question />,
      label: formatMessage(messages.common.help),
      path: '/help',
    },
  ];

  const bottomNavItems: NavItem[] = [
    {
      icon: <Plus />,
      label: formatMessage(messages.stations.addAStation),
      path: '/app/settings/services/',
    },
    {
      icon: <Gear />,
      label: formatMessage(messages.settings.settings),
      path: '/app/settings/',
      matchPath: '/app/settings/',
    },
    {
      icon: <At />,
      label: formatMessage(messages.settings.contactUs),
      path: 'mailto:contact@smartmixin.io',
    },
  ];

  const isSelected = (item: NavItem): boolean => {
    if (item.matchPath) {
      // Special handling for stations to exclude search
      if (item.matchPath === '/app/stations/') {
        return (
          pathname.startsWith('/app/stations/') && !pathname.includes('search')
        );
      }
      return pathname.startsWith(item.matchPath);
    }
    return pathname === item.path;
  };

  const handleNavClick = (path: string) => {
    if (path.startsWith('mailto:')) {
      // eslint-disable-next-line react-hooks/immutability -- handleNavClick only runs from onClick handlers, never during render
      window.location.href = path;
    } else {
      navigate(path);
    }
  };

  const renderNavItem = (item: NavItem, index: number) => {
    const selected = isSelected(item);

    if (!expanded) {
      return (
        <ListItem key={index} disablePadding sx={{px: 1, py: 0.5}}>
          <Tooltip title={item.label} placement="right" arrow>
            <ListItemButton
              onClick={() => handleNavClick(item.path)}
              selected={selected}
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 2,
              }}>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  color: selected ? 'primary.main' : 'text.secondary',
                }}>
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <ListItem key={index} disablePadding sx={{px: 1, py: 0.5}}>
        <ListItemButton
          onClick={() => handleNavClick(item.path)}
          selected={selected}
          sx={{minHeight: 48}}>
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: selected ? 'primary.main' : 'text.secondary',
            }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            slotProps={{
              primary: {
                noWrap: true,
                sx: {
                  fontSize: '0.875rem',
                  fontWeight: selected ? 500 : 400,
                },
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: railWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: railWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          backgroundColor: theme.palette.background.paper,
        },
      }}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'space-between' : 'center',
          px: expanded ? 2 : 1,
        }}>
        {expanded ? (
          <>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Logo size={28} />
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  color: 'text.primary',
                }}>
                SmartMixin
              </Box>
            </Box>
            <IconButton onClick={onToggle} size="small">
              <CaretLeft />
            </IconButton>
          </>
        ) : (
          <IconButton onClick={onToggle} size="small">
            <CaretRight />
          </IconButton>
        )}
      </Toolbar>

      <Divider />

      {/* Main navigation */}
      <List sx={{pt: 1}}>{mainNavItems.map(renderNavItem)}</List>

      {/* Admin navigation */}
      {isAdmin && (
        <>
          <Divider sx={{mx: 2}} />
          <List>
            {[
              {
                icon: <Bug />,
                label: 'Debug',
                path: '/app/admin/debug',
                matchPath: '/app/admin/',
              },
            ].map(renderNavItem)}
          </List>
        </>
      )}

      {/* Spacer */}
      <Box sx={{flexGrow: 1}} />

      {/* Bottom navigation */}
      <List sx={{pb: 1}}>
        {bottomNavItems.map(renderNavItem)}
        <Divider sx={{my: 1, mx: 2}} />
        {isLoggedIn && (
          <ListItem disablePadding sx={{px: 1, py: 0.5}}>
            {!expanded ? (
              <Tooltip
                title={formatMessage(messages.auth.logout)}
                placement="right"
                arrow>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    minHeight: 48,
                    justifyContent: 'center',
                    px: 2,
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                    },
                  }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: 'inherit',
                    }}>
                    <SignOut />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  minHeight: 48,
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                  },
                }}>
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: 'inherit',
                  }}>
                  <SignOut />
                </ListItemIcon>
                <ListItemText
                  primary={formatMessage(messages.auth.logout)}
                  slotProps={{
                    primary: {
                      noWrap: true,
                      sx: {fontSize: '0.875rem'},
                    },
                  }}
                />
              </ListItemButton>
            )}
          </ListItem>
        )}
      </List>
    </Drawer>
  );
};

export default NavRail;
